import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Bars3Icon, MoonIcon, SunIcon, XMarkIcon } from '@heroicons/react/24/outline'

import Logo from '../../media/logo.svg'
import useGlobalTheme, { broadcastThemeChange } from '../../hooks/useGlobalTheme'

const chromeWebstoreUrl =
	'https://chromewebstore.google.com/detail/loadlineai/cpcnkckpbpefgkcilhclfdefccflgaif'

const THEME_KEY = 'loadboard:theme'

function NavBar({ isNotLandingPage }) {
	const [isMenuOpen, setIsMenuOpen] = useState(false)
	const theme = useGlobalTheme()
	const isLight = theme === 'light'

	const toggleTheme = () => {
		const next = isLight ? 'dark' : 'light'
		try {
			localStorage.setItem(THEME_KEY, next)
		} catch {
			// non-fatal
		}
		broadcastThemeChange(next)
	}

	return (
		<div>
			<div className="w-full">
				<nav className={isLight ? "bg-white" : (isNotLandingPage ? "bg-[#15202b]" : "bg-[#001b2e]")}>
					<div className="px-6 sm:px-8 lg:px-10 navbar-content max-w-7xl mx-auto">
						<div className="flex justify-between items-center h-20 navbar-inner">
							<div className="flex items-center">
								<Link to="/" className="flex flex-col group navbar-logo">
									<div className="flex items-center">
										<img
											src={Logo}
											alt="LoadLine Logo"
											className="h-6 w-auto mr-1 transition-transform group-hover:scale-105"
										/>
										<span className={`text-xl font-semibold navbar-logo-text ${isLight ? 'text-gray-900' : 'text-[#eef3f9]'}`}>
											LoadLine
										</span>
									</div>
								</Link>
							</div>

							<div className="flex items-center space-x-4">
								{/* Theme toggle (always visible) */}
								<button
									type="button"
									onClick={toggleTheme}
									aria-label={isLight ? 'Switch to dark mode' : 'Switch to light mode'}
									title={isLight ? 'Switch to dark mode' : 'Switch to light mode'}
									className={`p-1.5 rounded-full transition-colors duration-200 border ${isLight ? 'text-gray-700 border-gray-300 hover:bg-gray-100' : 'text-[#eef3f9] border-[#004d7a] hover:bg-[#003456]'}`}
								>
									{isLight ? (
										<MoonIcon className="h-4 w-4" />
									) : (
										<SunIcon className="h-4 w-4" />
									)}
								</button>

								{!isNotLandingPage && (
									<>
										{/* Desktop Navigation */}
										<div className="hidden sm:flex items-center space-x-4 navbar-buttons">
											<Link
												to="/login"
												className={`text-base font-medium px-6 py-2.5 rounded-full transition-all duration-200 border navbar-login-button ${isLight ? 'bg-white text-gray-900 border-gray-300 hover:bg-gray-50' : 'bg-[#003456] text-[#eef3f9] border-[#004d7a] hover:bg-[#003f63]'}`}
											>
												Login
											</Link>

											<a
												href={chromeWebstoreUrl}
												target="_blank"
												rel="noopener noreferrer"
												className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-7 py-2.5 rounded-full text-base font-semibold hover:from-blue-700 hover:to-indigo-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center gap-2 navbar-chrome-button">

												<svg
													className="w-5 h-5"
													fill="currentColor"
													viewBox="0 0 32 32"
												>
													<path d="M21.25 11.031h-10.063c-0.969 0-1.844 0.313-2.625 0.781-1.344 0.844-2.25 2.344-2.313 4.031l-3.875-6.719c2.063-2.625 5.219-4.344 8.813-4.344 4.406 0 8.25 2.563 10.063 6.25zM1.875 9.75l5 8.688c0.031 0.031 0.031 0.063 0.063 0.094 0.438 0.781 1.125 1.406 1.938 1.844 0.719 0.344 1.469 0.594 2.313 0.594s1.625-0.25 2.344-0.594l-3.906 6.719c-5.438-0.781-9.625-5.438-9.625-11.094 0-2.313 0.688-4.469 1.875-6.25zM13.813 11.813h7.781c0.5 1.281 0.813 2.719 0.813 4.188 0 6.188-5.031 11.188-11.219 11.188-0.25 0-0.469 0-0.719-0.031l5-8.625c0.031-0.031 0.031-0.063 0.063-0.094 0.406-0.75 0.625-1.563 0.625-2.438v-0.156c-0.063-1.688-1-3.188-2.344-4.031zM7.469 16c0-2.094 1.625-3.75 3.719-3.75s3.75 1.656 3.75 3.75-1.656 3.75-3.75 3.75-3.719-1.656-3.719-3.75z" />
												</svg>
												<span>Add to Chrome</span>
											</a>
										</div>

										{/* Mobile Navigation */}
										<div className="sm:hidden">
											<button
												onClick={() => setIsMenuOpen(!isMenuOpen)}
												className={`p-2 rounded-full transition-colors duration-200 ${isLight ? 'text-gray-700 hover:bg-gray-100' : 'text-[#eef3f9] hover:bg-[#003456]'}`}
											>
												{isMenuOpen ? (
													<XMarkIcon className="h-6 w-6" />
												) : (
													<Bars3Icon className="h-6 w-6" />
												)}
											</button>
										</div>
									</>
								)}
							</div>
						</div>
					</div>
				</nav>
			</div>
			
			{/* Mobile Menu */}
			{!isNotLandingPage && isMenuOpen && (
				<div 
					className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 sm:hidden"
					onClick={() => setIsMenuOpen(false)}
				>
					{/* Close Button */}
					<div className="absolute top-9 right-6">
						<button
							onClick={() => setIsMenuOpen(false)}
							className={`p-3 rounded-full transition-colors duration-200 shadow-xl ${isLight ? 'bg-white text-gray-900 border border-gray-300 hover:bg-gray-50' : 'text-white bg-[#003456] hover:bg-[#003f63]'}`}
						>
							<XMarkIcon className="h-6 w-6" />
						</button>
					</div>

					<div
						className="absolute top-28 left-4 right-4 space-y-3"
						onClick={(e) => e.stopPropagation()}
					>
						<button
							type="button"
							onClick={() => {
								toggleTheme()
								setIsMenuOpen(false)
							}}
							className={`flex w-full items-center justify-center gap-2 font-medium px-8 py-4 rounded-full transition-all duration-200 border shadow-2xl ${isLight ? 'bg-white text-gray-900 border-gray-300 hover:bg-gray-50' : 'bg-[#003456] text-[#eef3f9] border-[#004d7a] hover:bg-[#003f63]'}`}
						>
							{isLight ? (
								<MoonIcon className="h-5 w-5" />
							) : (
								<SunIcon className="h-5 w-5" />
							)}
							<span>{isLight ? 'Dark mode' : 'Light mode'}</span>
						</button>
						<Link
							to="/login"
							className={`block w-full text-center font-medium px-8 py-4 rounded-full transition-all duration-200 border shadow-2xl ${isLight ? 'bg-white text-gray-900 border-gray-300 hover:bg-gray-50' : 'bg-[#003456] text-[#eef3f9] border-[#004d7a] hover:bg-[#003f63]'}`}
							onClick={() => setIsMenuOpen(false)}
						>
							Login
						</Link>

						<a
							href={chromeWebstoreUrl}
							target="_blank"
							rel="noopener noreferrer"
							className="block w-full text-center bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-8 py-4 rounded-full font-semibold hover:from-blue-700 hover:to-indigo-800 transition-all duration-200 shadow-2xl"
							onClick={() => setIsMenuOpen(false)}
						>
							Add to Chrome
						</a>
					</div>
				</div>
			)}
		</div>
	)
}

export default NavBar